import styled, { keyframes, css } from "styled-components";
import { motion } from "framer-motion";
import React, { useState, useEffect, useMemo, useRef } from "react";
import mixpanel from "mixpanel-browser";
import { HEATWAVE_SEO } from "../../../seo/pageSeoMeta";
import { Helmet } from "react-helmet-async";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Sun,
  Thermometer,
  Flame,
  TrendingUp,
  TrendingDown,
  Award,
  CircleHelp,
  LineChart,
  MapPin,
  Clock,
  Percent,
} from "lucide-react";
import TemperatureDelayScatter from "./TemperatureDelayScatter";
import DelayTemperatureTimeSeries from "./DelayTemperatureTimeSeries";
import HeatwaveStationMap from "./HeatwaveStationMap";
import heatwaveCountrySensitivityCsv from "../../../data/HeatwaveJune2026/heatwave_country_sensitivity_summary.csv";
import { StyledTooltip } from "../../TooltipStyles";

const colors = {
  skyDeep: "#4A9FD4",
  skyMid: "#7EC8E3",
  skyLight: "#B8E4F9",
  skyPale: "#E8F6FC",
  textDark: "#0f2744",
  textMid: "#2d4a6a",
  textMuted: "#5a7a94",
  slate300: "#CBD5E1",
  slate400: "#94A3B8",
  cardBg: "rgba(255, 255, 255, 0.82)",
  cardBorder: "rgba(255, 255, 255, 0.95)",
  cardShadow: "rgba(74, 159, 212, 0.15)",
  yellow400: "#FACC15",
  yellow500: "#EAB308",
  orange400: "#FB923C",
  orange500: "#F97316",
  red400: "#F87171",
  red500: "#EF4444",
  red600: "#DC2626",
  red700: "#B91C1C",
  emerald400: "#34D399",
  emerald500: "#10B981",
};

const HEAT_STATS = {
  hotDayAvgDelayMin: 5.9,
  hotDayCancellationPct: 4,
  coolDayAvgDelayMin: 4.2,
  coolDayCancellationPct: 2.5,
};

type CountryCode = "DE" | "AT" | "BE" | "FR" | "NL" | "CH" | "IT" | "HU";

type CountrySensitivity = {
  code: CountryCode;
  delayIncreasePct: number | null;
  cancellationIncreasePct: number | null;
  status: "ready" | "comingSoon";
  delayBelowS: number | null;
  delayDeltaS: number | null;
  delayMultiplier: number | null;
};

const COUNTRY_CODES: CountryCode[] = ["AT", "BE", "CH", "DE", "FR", "HU", "IT", "NL"];

const countryFlags: Record<CountryCode, string> = {
  DE: "🇩🇪",
  AT: "🇦🇹",
  BE: "🇧🇪",
  FR: "🇫🇷",
  NL: "🇳🇱",
  CH: "🇨🇭",
  IT: "🇮🇹",
  HU: "🇭🇺",
};

type SensitivityRow = {
  stopcountry: string;
  delay_multiplier: number;
  cancel_multiplier: number | null;
  delay_below_30_s: number;
  delay_above_30_s: number;
};

function parseSensitivityCsv(text: string): SensitivityRow[] {
  const lines = text.trim().split(/\r?\n/);
  if (lines.length < 2) return [];

  const headers = lines[0].split(",").map((h) => h.trim().replace(/^\uFEFF/, ""));
  return lines.slice(1).map((line) => {
    const values = line.split(",").map((v) => v.trim());
    const row: Record<string, string> = {};
    headers.forEach((header, i) => {
      row[header] = values[i] ?? "";
    });

    const delayMultiplier = parseFloat(row.delay_multiplier);
    const cancelRaw = row.cancel_multiplier;
    const cancelMultiplier =
      cancelRaw === "" || cancelRaw == null ? null : parseFloat(cancelRaw);

    return {
      stopcountry: row.stopcountry,
      delay_multiplier: delayMultiplier,
      cancel_multiplier:
        cancelMultiplier == null || isNaN(cancelMultiplier) ? null : cancelMultiplier,
      delay_below_30_s: parseFloat(row.delay_below_30_s),
      delay_above_30_s: parseFloat(row.delay_above_30_s),
    };
  });
}

function multiplierToIncreasePct(multiplier: number): number {
  return Math.round((multiplier - 1) * 100);
}

function heatPctIntensity(pct: number, maxPct: number): number {
  if (maxPct <= 0) return 0;
  return Math.min(1, Math.max(0, pct / maxPct));
}

const pulse = keyframes`
  0%, 100% { opacity: 1; }
  50% { opacity: 0.4; }
`;

const sunPulse = keyframes`
  0%, 100% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.04);
  }
`;

const sunGlow = keyframes`
  0%, 100% {
    box-shadow:
      0 0 60px 30px rgba(255, 213, 79, 0.55),
      0 0 100px 50px rgba(255, 152, 0, 0.35),
      0 0 140px 70px rgba(255, 87, 34, 0.15);
  }
  50% {
    box-shadow:
      0 0 80px 40px rgba(255, 213, 79, 0.65),
      0 0 120px 60px rgba(255, 152, 0, 0.45),
      0 0 160px 80px rgba(255, 87, 34, 0.2);
  }
`;

const sunRotate = keyframes`
  from { transform: translate(-50%, -50%) rotate(0deg); }
  to { transform: translate(-50%, -50%) rotate(360deg); }
`;

const heatGradientShift = keyframes`
  0%   { background-position: 0% 0%; }
  50%  { background-position: 100% 100%; }
  100% { background-position: 0% 0%; }
`;

const SUN_RAY_CENTER = 120;
const SUN_RAY_INNER = 88;
const SUN_RAY_OUTER_MAX = 205;

const SUN_RAY_SPECS: {
  angle: number;
  length: number;
  width: number;
  opacity: number;
}[] = [
  { angle:   0, length: 0.92, width: 9,  opacity: 0.24 },
  { angle:   9, length: 0.55, width: 5,  opacity: 0.14 },
  { angle:  14, length: 0.78, width: 7,  opacity: 0.20 },
  { angle:  24, length: 0.45, width: 4,  opacity: 0.12 },
  { angle:  35, length: 0.88, width: 9,  opacity: 0.23 },
  { angle:  40, length: 0.60, width: 5,  opacity: 0.16 },
  { angle:  50, length: 0.72, width: 7,  opacity: 0.19 },
  { angle:  61, length: 0.42, width: 4,  opacity: 0.11 },
  { angle:  68, length: 0.95, width: 10, opacity: 0.25 },
  { angle:  73, length: 0.52, width: 5,  opacity: 0.13 },
  { angle:  83, length: 0.82, width: 8,  opacity: 0.21 },
  { angle:  91, length: 0.48, width: 4,  opacity: 0.12 },
  { angle: 100, length: 0.90, width: 9,  opacity: 0.24 },
  { angle: 109, length: 0.58, width: 6,  opacity: 0.15 },
  { angle: 116, length: 0.75, width: 7,  opacity: 0.20 },
  { angle: 125, length: 0.44, width: 4,  opacity: 0.12 },
  { angle: 132, length: 0.85, width: 8,  opacity: 0.22 },
  { angle: 143, length: 0.62, width: 6,  opacity: 0.16 },
  { angle: 148, length: 0.70, width: 7,  opacity: 0.18 },
  { angle: 158, length: 0.46, width: 4,  opacity: 0.12 },
  { angle: 166, length: 0.93, width: 10, opacity: 0.25 },
  { angle: 175, length: 0.54, width: 5,  opacity: 0.14 },
  { angle: 180, length: 0.80, width: 7,  opacity: 0.21 },
  { angle: 191, length: 0.50, width: 4,  opacity: 0.13 },
  { angle: 199, length: 0.88, width: 9,  opacity: 0.23 },
  { angle: 206, length: 0.60, width: 6,  opacity: 0.16 },
  { angle: 217, length: 0.74, width: 7,  opacity: 0.19 },
  { angle: 222, length: 0.43, width: 4,  opacity: 0.11 },
  { angle: 232, length: 0.91, width: 9,  opacity: 0.24 },
  { angle: 241, length: 0.56, width: 5,  opacity: 0.14 },
  { angle: 246, length: 0.77, width: 7,  opacity: 0.20 },
  { angle: 257, length: 0.47, width: 4,  opacity: 0.12 },
  { angle: 264, length: 0.86, width: 8,  opacity: 0.22 },
  { angle: 273, length: 0.63, width: 6,  opacity: 0.16 },
  { angle: 282, length: 0.73, width: 7,  opacity: 0.19 },
  { angle: 289, length: 0.41, width: 4,  opacity: 0.11 },
  { angle: 298, length: 0.94, width: 10, opacity: 0.25 },
  { angle: 307, length: 0.53, width: 5,  opacity: 0.13 },
  { angle: 312, length: 0.81, width: 8,  opacity: 0.21 },
  { angle: 322, length: 0.49, width: 4,  opacity: 0.12 },
  { angle: 331, length: 0.89, width: 9,  opacity: 0.23 },
  { angle: 340, length: 0.59, width: 6,  opacity: 0.15 },
  { angle: 346, length: 0.76, width: 7,  opacity: 0.20 },
  { angle: 355, length: 0.45, width: 4,  opacity: 0.12 },
];

function sunRayEndpoints(angleDeg: number, length: number) {
  const rad = ((angleDeg - 90) * Math.PI) / 180;
  const outerR = SUN_RAY_INNER + (SUN_RAY_OUTER_MAX - SUN_RAY_INNER) * length;
  return {
    x1: SUN_RAY_CENTER + Math.cos(rad) * SUN_RAY_INNER,
    y1: SUN_RAY_CENTER + Math.sin(rad) * SUN_RAY_INNER,
    x2: SUN_RAY_CENTER + Math.cos(rad) * outerR,
    y2: SUN_RAY_CENTER + Math.sin(rad) * outerR,
  };
}

const SunRaysSvg = styled.svg`
  position: absolute;
  top: 50%;
  left: 50%;
  width: 210%;
  height: 210%;
  overflow: visible;
  pointer-events: none;
  animation: ${sunRotate} 110s linear infinite;
  /* CSS blur is GPU-composited in all browsers; SVG feGaussianBlur inside a
     rotating element forces per-frame CPU rasterisation in Firefox. */
  filter: blur(26px);
  will-change: transform;
`;

function SunRays() {
  return (
    <SunRaysSvg viewBox="0 0 240 240" aria-hidden>
      {SUN_RAY_SPECS.map((ray, index) => {
        const { x1, y1, x2, y2 } = sunRayEndpoints(ray.angle, ray.length);
        return (
          <line
            key={index}
            x1={x1}
            y1={y1}
            x2={x2}
            y2={y2}
            stroke={`rgba(255, 210, 90, ${ray.opacity})`}
            strokeWidth={ray.width}
            strokeLinecap="round"
          />
        );
      })}
    </SunRaysSvg>
  );
}

const PageContainer = styled.div`
  min-height: 100vh;
  background: linear-gradient(
    180deg,
    ${colors.skyDeep} 0%,
    ${colors.skyMid} 30%,
    ${colors.skyLight} 65%,
    ${colors.skyPale} 100%
  );
  color: ${colors.textDark};
  position: relative;
  font-family: "Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif;
  -webkit-font-smoothing: antialiased;

  * {
    font-family: inherit;
  }
`;

const ScorchingSun = styled.div`
  position: fixed;
  top: -60px;
  right: -60px;
  width: 260px;
  height: 260px;
  overflow: visible;
  animation: ${sunPulse} 5s ease-in-out infinite;
  z-index: 1;
  pointer-events: none;

  @media (min-width: 768px) {
    top: -80px;
    right: -80px;
    width: 320px;
    height: 320px;
  }
`;

const SunCore = styled.div`
  position: absolute;
  inset: 0;
  border-radius: 50%;
  background: radial-gradient(
    circle at 40% 40%,
    #fffde7 0%,
    #ffeb3b 18%,
    #ffc107 40%,
    #ff9800 58%,
    rgba(255, 152, 0, 0.4) 72%,
    transparent 78%
  );
  animation: ${sunGlow} 5s ease-in-out infinite;
  pointer-events: none;
`;

const SkyOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: radial-gradient(ellipse at 85% 8%, rgba(255, 235, 59, 0.12) 0%, transparent 45%);
  pointer-events: none;
  z-index: 0;
`;

const ScrollProgress = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  height: 4px;
  width: 0%;
  background: linear-gradient(to right, ${colors.yellow400}, ${colors.orange500}, ${colors.red500});
  z-index: 1001;
  will-change: width;
`;

const Section = styled.section`
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 5rem 1rem;
  position: relative;
  overflow: hidden;
  z-index: 2;

  @media (min-width: 768px) {
    padding: 5rem 2rem;
  }
`;

const ClosingSection = styled(Section)`
  &::before {
    content: "";
    position: absolute;
    top: -100px;
    left: -100px;
    right: -100px;
    bottom: 0;
    background: linear-gradient(to top, rgba(255, 152, 0, 0.08) 0%, transparent 50%);
    pointer-events: none;
    z-index: 0;
  }
`;

const HeroSection = styled(Section)`
  min-height: 70vh;

  @media (min-width: 768px) {
    min-height: 80vh;
  }
`;

const CloudField = styled.div`
  position: absolute;
  inset: 0;
  pointer-events: none;
  overflow: hidden;

  div {
    position: absolute;
    border-radius: 50%;
    background: rgba(255, 255, 255, 0.45);
    filter: blur(20px);
    animation: ${pulse} 6s ease-in-out infinite;
    /* Force immediate GPU layer creation to prevent Safari from showing
       compositing-layer rectangle artifacts during lazy layer promotion. */
    transform: translateZ(0);
    -webkit-transform: translateZ(0);
  }
`;

const HeroContent = styled.div`
  text-align: center;
  z-index: 10;
  max-width: 56rem;
  position: relative;
`;

const HeroTitle = styled.h1`
  font-size: 4rem;
  font-weight: 700;
  margin-bottom: 1.5rem;

  @media (min-width: 768px) {
    font-size: 6rem;
  }
`;

const HeroTitlePrefix = styled.span`
  background: linear-gradient(to right, ${colors.yellow400}, ${colors.orange400});
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
`;

// Extracted into its own memo component so parent re-renders never touch the
// <animate> DOM nodes, which would reset the SMIL animation timing mid-flight.
const MeltSvgFilter = React.memo(() => (
  <svg style={{ position: "absolute", width: 0, height: 0, overflow: "hidden" }} aria-hidden>
    <defs>
      <filter id="melt-filter" x="-5%" y="-5%" width="110%" height="400%">
        <feTurbulence
          type="fractalNoise"
          baseFrequency="0.035 0.006"
          numOctaves="2"
          seed="4"
          result="noise"
        />
        <feColorMatrix
          in="noise"
          type="matrix"
          values="1 0 0 0 0  0 1 0 0 0.4  0 0 1 0 0  0 0 0 1 0"
          result="biasedNoise"
        />
        <feDisplacementMap
          in="SourceGraphic"
          in2="biasedNoise"
          xChannelSelector="R"
          yChannelSelector="G"
          scale="0"
          result="displaced"
        >
          <animate
            attributeName="scale"
            values="0;-36"
            keyTimes="0;1"
            dur="8s"
            begin="1s"
            fill="freeze"
          />
        </feDisplacementMap>
        <feGaussianBlur in="displaced" stdDeviation="0" result="blurred">
          <animate
            attributeName="stdDeviation"
            values="0;2"
            keyTimes="0;1"
            dur="8s"
            begin="1s"
            fill="freeze"
          />
        </feGaussianBlur>
        <feColorMatrix in="blurred" type="matrix" values="1 0 0 0 0  0 1 0 0 0  0 0 1 0 0  0 0 0 1 0">
          <animate
            attributeName="values"
            values="1 0 0 0 0  0 1 0 0 0  0 0 1 0 0  0 0 0 1 0;1 0 0 0 0  0 1 0 0 0  0 0 1 0 0  0 0 0 14 -5"
            keyTimes="0;1"
            dur="8s"
            begin="1s"
            fill="freeze"
          />
        </feColorMatrix>
      </filter>
    </defs>
  </svg>
));

const MeltWord = styled.span`
  display: inline-block;
  filter: url(#melt-filter);
  background: linear-gradient(to right, ${colors.orange400}, ${colors.red600});
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  transform-origin: center bottom;
`;

const HeroSubtitle = styled.p`
  font-size: 1.25rem;
  color: ${colors.textMid};
  margin-bottom: 2rem;

  @media (min-width: 768px) {
    font-size: 1.5rem;
  }
`;

const ScrollHint = styled.div`
  font-size: 0.875rem;
  color: white;
`;

const DataDisclaimer = styled.p`
  margin: 2rem auto 0;
  max-width: 42rem;
  font-size: 0.875rem;
  line-height: 1.6;
  color: white;
  text-align: center;
`;

const ContentCard = styled.div<{ visible?: boolean }>`
  max-width: 56rem;
  width: 100%;
  margin: 0 auto;
  text-align: center;
  transform: ${({ visible }) => (visible ? "translateY(0)" : "translateY(30px)")};
  opacity: ${({ visible }) => (visible ? 1 : 0)};
  transition: all 1s ease;
`;

const SectionWrapper = styled.div<{ maxWidth?: string }>`
  max-width: ${({ maxWidth }) => maxWidth || "56rem"};
  width: 100%;
  margin: 0 auto;
`;

const SectionTitle = styled.h2`
  font-size: 1.875rem;
  font-weight: 700;
  margin-bottom: 1rem;
  color: ${colors.textDark};

  @media (min-width: 768px) {
    font-size: 3rem;
  }
`;

const SectionSubtitle = styled.p`
  font-size: 1.25rem;
  color: ${colors.textMid};
  margin-bottom: 3rem;
`;

const BigNumberCard = styled.div`
  position: relative;
  isolation: isolate;
  border-radius: 1.5rem;
  padding: 2rem;
  overflow: hidden;
  box-shadow:
    inset 0 0 0 1px rgba(255, 255, 255, 0.28),
    0 8px 32px rgba(249, 115, 22, 0.35);

  &::before {
    content: "";
    position: absolute;
    inset: 0;
    border-radius: inherit;
    background: linear-gradient(
      to bottom,
      ${colors.yellow400} 0%,
      ${colors.orange500} 45%,
      ${colors.red500} 100%
    );
    z-index: 0;
  }

  > * {
    position: relative;
    z-index: 1;
  }

  @media (min-width: 768px) {
    padding: 3rem;
  }
`;

const BigNumber = styled.div`
  font-size: 4.5rem;
  font-weight: 900;
  margin-bottom: 1rem;
  color: white;

  @media (min-width: 768px) {
    font-size: 6rem;
  }
`;

const BigNumberDescription = styled.p`
  font-size: 1.25rem;
  color: rgba(255, 255, 255, 0.95);
  margin-bottom: 0;
`;

const KpiGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
  margin-top: 1.75rem;

  @media (min-width: 768px) {
    gap: 1.25rem;
    margin-top: 2rem;
  }
`;

const KpiCard = styled.div`
  background: ${colors.cardBg};
  border-radius: 1rem;
  padding: 1.25rem 1.5rem;
  border: 1px solid ${colors.cardBorder};
  box-shadow: 0 4px 16px ${colors.cardShadow};
  backdrop-filter: blur(8px);
`;

const KpiValue = styled.div`
  font-size: 2rem;
  font-weight: 800;
  color: ${colors.orange400};
  line-height: 1.1;

  @media (min-width: 768px) {
    font-size: 2.5rem;
  }
`;

const KpiLabel = styled.div`
  font-size: 0.875rem;
  color: ${colors.textMuted};
  margin-top: 0.35rem;
`;

const StatGrid = styled.div<{ columns?: number }>`
  display: grid;
  grid-template-columns: repeat(${({ columns }) => columns || 3}, 1fr);
  gap: 1rem;
  margin-top: 3rem;

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }

  @media (min-width: 768px) {
    gap: 2rem;
  }
`;

const StatCard = styled.div`
  background: ${colors.cardBg};
  border-radius: 0.75rem;
  padding: 1rem;
  border: 1px solid ${colors.cardBorder};
  box-shadow: 0 4px 16px ${colors.cardShadow};
  backdrop-filter: blur(8px);

  @media (min-width: 768px) {
    padding: 1.5rem;
  }
`;

const StatValue = styled.div<{ color: string }>`
  font-size: 1.875rem;
  font-weight: 700;
  color: ${({ color }) => color};

  @media (min-width: 768px) {
    font-size: 2.25rem;
  }
`;

const StatLabel = styled.div`
  font-size: 0.875rem;
  color: ${colors.textMuted};
  margin-top: 0.5rem;

  @media (min-width: 768px) {
    font-size: 1rem;
  }
`;

const StatLabelWithInfo = styled(StatLabel)`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.35rem;
`;

const CountryStatLabel = styled.div`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.3rem;
  font-size: 0.75rem;
  color: ${colors.textMuted};
`;

const InfoIconButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  margin: 0;
  border: none;
  background: none;
  color: ${colors.textMuted};
  cursor: help;
  line-height: 0;
  flex-shrink: 0;

  &:hover {
    color: ${colors.textMid};
  }
`;

const ComparisonGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 2rem;
  width: 100%;

  @media (min-width: 768px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
`;

const DayCard = styled.div<{ variant: "hot" | "cool" }>`
  width: 100%;
  min-width: 0;
  box-sizing: border-box;
  background: ${({ variant }) =>
    variant === "hot"
      ? "linear-gradient(to bottom right, rgba(255, 220, 130, 0.97), rgba(255, 165, 60, 0.93))"
      : `linear-gradient(to bottom right, ${colors.cardBg}, rgba(220, 240, 255, 0.9))`};
  border-radius: 1.5rem;
  padding: 2rem;
  border: 1px solid
    ${({ variant }) => (variant === "hot" ? "rgba(249, 115, 22, 0.6)" : "rgba(255, 255, 255, 0.95)")};
  box-shadow: ${({ variant }) =>
    variant === "hot"
      ? `0 8px 24px rgba(249, 115, 22, 0.3)`
      : `0 8px 24px ${colors.cardShadow}`};
  backdrop-filter: blur(8px);
  text-align: center;

  @media (min-width: 768px) {
    padding: 2.5rem;
  }
`;

const DayLabel = styled.div<{ variant?: "hot" | "cool" }>`
  font-size: 2.5rem;
  font-weight: 900;
  margin-bottom: 0.5rem;
  color: ${({ variant }) => (variant === "hot" ? colors.orange500 : colors.skyDeep)};

  @media (min-width: 768px) {
    font-size: 3rem;
  }
`;

const DayTitle = styled.div`
  font-size: 1.25rem;
  font-weight: 700;
  margin-bottom: 2rem;
  color: ${colors.textMid};
`;

const DayStatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1rem;
`;

const DayStatCard = styled.div`
  background: rgba(255, 255, 255, 0.6);
  border-radius: 0.75rem;
  padding: 1rem;
  text-align: center;
`;

const ChartCard = styled.div`
  background: ${colors.cardBg};
  border-radius: 1.5rem;
  padding: 1.5rem;
  border: 1px solid ${colors.cardBorder};
  box-shadow: 0 8px 32px ${colors.cardShadow};
  backdrop-filter: blur(12px);

  @media (min-width: 768px) {
    padding: 2.5rem;
  }
`;

const DiveDeepMetricNotes = styled.div`
  margin: 2rem auto 0;
  max-width: 42rem;
  font-size: 0.8125rem;
  line-height: 1.55;
  color: ${colors.textMuted};
  text-align: center;

  p + p {
    margin-top: 0.35rem;
  }
`;

const InfoBox = styled.div`
  background: ${colors.cardBg};
  border-radius: 0.75rem;
  padding: 1.5rem;
  border: 1px solid ${colors.cardBorder};
  box-shadow: 0 4px 16px ${colors.cardShadow};
  text-align: left;
  margin-top: 2rem;
  backdrop-filter: blur(8px);
`;

const InfoHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 0.75rem;
`;

const InfoTitle = styled.div`
  font-weight: 700;
  font-size: 1.125rem;
  color: ${colors.orange400};
`;

const InfoText = styled.p`
  color: ${colors.textMid};
  line-height: 1.6;
  margin: 0;
`;

const SensitivityGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 2rem;
  margin-bottom: 3rem;

  @media (min-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
  }
`;

const SensitivityCard = styled.div<{ variant: "resilient" | "sensitive"; visible: boolean }>`
  background: ${({ variant }) =>
    variant === "resilient"
      ? "linear-gradient(to bottom right, rgba(74, 159, 212, 0.2), rgba(126, 200, 227, 0.14))"
      : "linear-gradient(to bottom right, rgba(185, 28, 28, 0.22), rgba(234, 88, 12, 0.16))"};
  border-radius: 1.5rem;
  padding: 1.5rem;
  border: 1px solid
    ${({ variant }) =>
      variant === "resilient" ? "rgba(74, 159, 212, 0.4)" : "rgba(220, 38, 38, 0.45)"};
  backdrop-filter: blur(8px);
  box-shadow: 0 8px 24px ${colors.cardShadow};
  transform: ${({ visible, variant }) =>
    visible ? "translateX(0)" : variant === "resilient" ? "translateX(-40px)" : "translateX(40px)"};
  opacity: ${({ visible }) => (visible ? 1 : 0)};
  transition: all 0.7s ease;
  transition-delay: ${({ variant }) => (variant === "sensitive" ? "300ms" : "0ms")};
  display: flex;
  flex-direction: column;
  will-change: transform;
`;

const SensitivityCardTitle = styled.div<{ variant: "resilient" | "sensitive" }>`
  font-size: 1.5rem;
  font-weight: 700;
  color: ${({ variant }) => (variant === "resilient" ? colors.skyDeep : colors.red600)};
`;

const SensitivityEmoji = styled.span`
  font-size: 2.5rem;
  line-height: 1;
`;

const SensitivityCountryName = styled.div`
  font-size: 2rem;
  font-weight: 900;
  margin-bottom: 0.5rem;
  color: ${colors.textDark};
`;

const SensitivityStatsStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  margin-bottom: 1rem;
`;

const SensitivityDescription = styled.p`
  margin-top: auto;
  padding-top: 1rem;
  border-top: 1px solid rgba(74, 159, 212, 0.2);
  color: ${colors.textMid};
  font-size: 0.875rem;
  line-height: 1.5;
`;

const CountryList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  max-width: 80rem;
  width: 100%;
`;

const CountryMetricSwitch = styled.div`
  display: inline-flex;
  margin-bottom: 1rem;
  background: rgba(255, 255, 255, 0.92);
  backdrop-filter: blur(8px);
  border: 1px solid rgba(126, 200, 227, 0.55);
  border-radius: 9999px;
  padding: 2px;
  box-shadow: 0 4px 16px ${colors.cardShadow};
  gap: 2px;
`;

const CountryMetricSwitchButton = styled.button<{ $active: boolean }>`
  display: flex;
  align-items: center;
  gap: 0.3rem;
  padding: 0.4rem 0.9rem;
  border-radius: 9999px;
  font-size: 0.8125rem;
  font-weight: ${({ $active }) => ($active ? "600" : "400")};
  color: ${({ $active }) => ($active ? colors.textDark : colors.textMuted)};
  background: ${({ $active }) => ($active ? "white" : "transparent")};
  border: none;
  cursor: pointer;
  transition: all 0.15s ease;
  box-shadow: ${({ $active }) => ($active ? "0 1px 4px rgba(0,0,0,0.12)" : "none")};
  white-space: nowrap;

  &:hover {
    color: ${colors.textDark};
  }
`;

const countryCardEnter = keyframes`
  from {
    transform: translateX(-50px);
  }
  to {
    transform: translateX(0);
  }
`;

const CountryCardShell = styled.div<{ visible: boolean; delay: number }>`
  opacity: ${({ visible }) => (visible ? 1 : 0)};
  animation: ${({ visible, delay }) =>
    visible ? css`${countryCardEnter} 0.7s ease ${delay}ms both` : "none"};
  will-change: transform;
`;

const CountryCard = styled.div<{ dimmed?: boolean }>`
  background: ${colors.cardBg};
  border-radius: 1rem;
  padding: 1.5rem;
  border: 1px solid ${colors.cardBorder};
  box-shadow: 0 4px 16px ${colors.cardShadow};
  backdrop-filter: blur(8px);
  opacity: ${({ dimmed }) => (dimmed ? 0.55 : 1)};
  will-change: transform;
`;

const CountryHeader = styled.div`
  display: grid;
  grid-template-columns: auto 1fr auto;
  align-items: center;
  gap: 1rem;

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
    gap: 0.75rem;
  }
`;

const RankNumber = styled.div<{ rank: number }>`
  font-size: 1.875rem;
  font-weight: 900;
  color: ${({ rank }) =>
    rank === 1
      ? colors.yellow400
      : rank === 2
        ? colors.slate300
        : rank === 3
          ? colors.orange500
          : colors.slate400};
  min-width: 3rem;
`;

const CountryInfo = styled.div`
  min-width: 0;
  text-align: left;

  div:first-child {
    font-weight: 700;
    font-size: 1.25rem;
    margin-bottom: 0.5rem;
    display: flex;
    align-items: center;
    gap: 0.5rem;
    flex-wrap: wrap;
  }
`;

const ComingSoonBadge = styled.span`
  font-size: 0.7rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  padding: 0.2rem 0.5rem;
  border-radius: 9999px;
  background: rgba(184, 228, 249, 0.6);
  color: ${colors.textMuted};
  border: 1px solid rgba(126, 200, 227, 0.5);
`;

const ProgressBar = styled.div`
  width: 100%;
  display: flex;
  height: 0.75rem;
`;

const ProgressFillBlue = styled.div<{ width: number; visible: boolean; delay: number }>`
  height: 0.75rem;
  border-radius: 9999px 0 0 9999px;
  background: #1e3a5f;
  flex-shrink: 0;
  width: ${({ visible, width }) => (visible ? `${width}%` : "0%")};
  transition: width 0.8s ease;
  transition-delay: ${({ delay }) => delay}ms;

  ${CountryCard}:hover & {
    transition-delay: 0ms;
  }
`;

const ProgressFill = styled.div<{ width: number; visible: boolean; delay: number }>`
  height: 0.75rem;
  border-radius: 0 9999px 9999px 0;
  background: linear-gradient(
    to right,
    ${colors.yellow400},
    ${colors.orange500} 55%,
    ${colors.red600} 85%,
    ${colors.red700}
  );
  background-repeat: no-repeat;
  background-position: left center;
  background-size: ${({ width }) => (width > 0 ? `${(100 / width) * 100}%` : "100%")} 100%;
  flex-shrink: 0;
  width: ${({ visible, width }) => (visible ? `${width}%` : "0%")};
  transition: width 0.8s ease;
  transition-delay: ${({ delay }) => delay}ms;

  ${CountryCard}:hover & {
    transition-delay: 0ms;
  }
`;

const CountryStats = styled.div`
  display: flex;
  gap: 1.5rem;
  text-align: center;
  min-width: 200px;
  justify-content: flex-end;

  @media (max-width: 640px) {
    justify-content: flex-start;
    min-width: auto;
  }
`;

const CountryStat = styled.div`
  min-width: 90px;
`;

const HeatPctValue = styled.div<{ $intensity: number }>`
  font-size: 1.5rem;
  font-weight: 800;
  line-height: 1.2;
  letter-spacing: -0.02em;
  background: linear-gradient(
    135deg,
    ${colors.yellow400} 0%,
    ${colors.orange500} 38%,
    ${colors.red500} 72%,
    ${colors.red700} 100%
  );
  background-size: 280% 100%;
  background-position: ${({ $intensity }) => `${$intensity * 82}% 0`};
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;

  @media (min-width: 768px) {
    font-size: 1.625rem;
  }
`;

const MutedPctValue = styled.div`
  font-size: 1.5rem;
  font-weight: 700;
  color: ${colors.textMuted};
`;

type DelayMetricLabelProps = {
  countryName: string;
  pct: number;
  tooltipId: string;
  variant?: "card" | "bar";
  minDiff?: number;
};

function DelayMetricLabel({
  countryName,
  pct,
  tooltipId,
  variant = "card",
  minDiff,
}: DelayMetricLabelProps) {
  const { t } = useTranslation();
  const tooltipText =
    minDiff != null
      ? t("heatwave.countries.delayIncreaseTooltipMin", {
          country: countryName,
          min: minDiff.toLocaleString(undefined, { minimumFractionDigits: 1, maximumFractionDigits: 1 }),
        })
      : t("heatwave.countries.delayIncreaseTooltip", {
          country: countryName,
          pct,
        });
  const Label = variant === "card" ? StatLabelWithInfo : CountryStatLabel;

  return (
    <>
      <Label>
        {t("heatwave.countries.delayIncrease")}
        <InfoIconButton
          type="button"
          aria-label={tooltipText}
          data-tooltip-id={tooltipId}
        >
          <CircleHelp size={variant === "card" ? 14 : 12} strokeWidth={2} />
        </InfoIconButton>
      </Label>
      <StyledTooltip
        id={tooltipId}
        place="top"
        className="tooltip-styled"
        content={tooltipText}
      />
    </>
  );
}

type CancellationMetricLabelProps = {
  countryName: string;
  pct: number;
  tooltipId: string;
  variant?: "card" | "bar";
};

function CancellationMetricLabel({
  countryName,
  pct,
  tooltipId,
  variant = "card",
}: CancellationMetricLabelProps) {
  const { t } = useTranslation();
  const tooltipText = t("heatwave.countries.cancellationIncreaseTooltip", {
    country: countryName,
    pct,
  });
  const Label = variant === "card" ? StatLabelWithInfo : CountryStatLabel;

  return (
    <>
      <Label>
        {t("heatwave.countries.cancellationIncrease")}
        <InfoIconButton
          type="button"
          aria-label={tooltipText}
          data-tooltip-id={tooltipId}
        >
          <CircleHelp size={variant === "card" ? 14 : 12} strokeWidth={2} />
        </InfoIconButton>
      </Label>
      <StyledTooltip
        id={tooltipId}
        place="top"
        className="tooltip-styled"
        content={tooltipText}
      />
    </>
  );
}

type CancellationUnavailableMetricLabelProps = {
  countryName: string;
  tooltipId: string;
  variant?: "card" | "bar";
};

function CancellationUnavailableMetricLabel({
  countryName,
  tooltipId,
  variant = "bar",
}: CancellationUnavailableMetricLabelProps) {
  const { t } = useTranslation();
  const tooltipText = t("heatwave.countries.cancellationUnavailableTooltip", {
    country: countryName,
  });
  const Label = variant === "card" ? StatLabelWithInfo : CountryStatLabel;

  return (
    <>
      <Label>
        {t("heatwave.countries.cancellationIncrease")}
        <InfoIconButton
          type="button"
          aria-label={tooltipText}
          data-tooltip-id={tooltipId}
        >
          <CircleHelp size={variant === "card" ? 14 : 12} strokeWidth={2} />
        </InfoIconButton>
      </Label>
      <StyledTooltip
        id={tooltipId}
        place="top"
        className="tooltip-styled"
        content={tooltipText}
      />
    </>
  );
}

const CTACard = styled.div`
  background: ${colors.cardBg};
  border-radius: 1rem;
  padding: 2.5rem;
  border: 1px solid ${colors.cardBorder};
  box-shadow: 0 8px 32px ${colors.cardShadow};
  backdrop-filter: blur(12px);
  margin-bottom: 2rem;
  text-align: center;
`;

const CTATitle = styled.h3`
  font-size: 1.875rem;
  font-weight: 700;
  color: ${colors.textDark};
  margin: 0 0 0.75rem;
`;

const CTASubtitle = styled.p`
  font-size: 1.125rem;
  color: ${colors.textMid};
  margin-bottom: 2rem;
  line-height: 1.6;
`;

const CTAButton = styled.button`
  background: linear-gradient(to right, ${colors.orange500}, ${colors.red500});
  color: white;
  border: none;
  border-radius: 0.75rem;
  padding: 1rem 2rem;
  font-size: 1.125rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  font-family: inherit;

  &:hover {
    background: linear-gradient(to right, ${colors.orange400}, ${colors.red400});
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(249, 115, 22, 0.4);
  }
`;

const SecondaryCTACard = styled(CTACard)`
  padding: 1.5rem 2rem;
`;

const SecondaryCTAButton = styled.button`
  background: rgba(255, 255, 255, 0.7);
  color: ${colors.textDark};
  border: 1px solid rgba(126, 200, 227, 0.6);
  border-radius: 0.5rem;
  padding: 0.75rem 1.5rem;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  font-family: inherit;

  &:hover {
    background: rgba(255, 255, 255, 0.95);
    transform: translateY(-1px);
  }
`;

const Icon = ({
  name,
  size = 24,
  color,
}: {
  name: string;
  size?: number;
  color?: string;
}) => {
  const icons: Record<string, React.ComponentType<{ size?: number; color?: string }>> = {
    sun: Sun,
    thermometer: Thermometer,
    flame: Flame,
    trendingUp: TrendingUp,
    trendingDown: TrendingDown,
    award: Award,
    lineChart: LineChart,
    mapPin: MapPin,
  };
  const IconComponent = icons[name];
  if (!IconComponent) return null;
  return <IconComponent size={size} color={color} />;
};

const HeatwavePage = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const fmtStat = (v: number) =>
    v.toLocaleString(i18n.language, { minimumFractionDigits: 1, maximumFractionDigits: 1 });
  const fmtStatInt = (v: number) =>
    v.toLocaleString(i18n.language, { maximumFractionDigits: 0 });
  const scrollProgressRef = useRef<HTMLDivElement>(null);
  const [visibleSections, setVisibleSections] = useState<Record<string, boolean>>({});
  const [sensitivityRows, setSensitivityRows] = useState<SensitivityRow[]>([]);
  const [countryBarMetric, setCountryBarMetric] = useState<"relative" | "minutes">("relative");

  const countryNames: Record<CountryCode, string> = useMemo(
    () => ({
      DE: t("heatwave.countryNames.germany"),
      AT: t("heatwave.countryNames.austria"),
      BE: t("heatwave.countryNames.belgium"),
      FR: t("heatwave.countryNames.france"),
      NL: t("heatwave.countryNames.netherlands"),
      CH: t("heatwave.countryNames.switzerland"),
      IT: t("heatwave.countryNames.italy"),
      HU: t("heatwave.countryNames.hungary"),
    }),
    [t]
  );

  useEffect(() => {
    const load = async () => {
      try {
        const response = await fetch(heatwaveCountrySensitivityCsv);
        if (!response.ok) {
          setSensitivityRows([]);
          return;
        }
        const text = await response.text();
        setSensitivityRows(
          parseSensitivityCsv(text).filter(
            (row) => row.stopcountry !== "ALL" && !isNaN(row.delay_multiplier)
          )
        );
      } catch {
        setSensitivityRows([]);
      }
    };
    load();
  }, []);

  const countrySensitivity: CountrySensitivity[] = useMemo(() => {
    const rowByCode = new Map(sensitivityRows.map((row) => [row.stopcountry, row]));

    return COUNTRY_CODES.map((code) => {
      const row = rowByCode.get(code);
      const hasDelay = row != null && !isNaN(row.delay_multiplier);
      const hasCancel = row?.cancel_multiplier != null && !isNaN(row.cancel_multiplier);

      return {
        code,
        delayIncreasePct: hasDelay ? multiplierToIncreasePct(row.delay_multiplier) : null,
        cancellationIncreasePct: hasCancel
          ? multiplierToIncreasePct(row.cancel_multiplier!)
          : null,
        status: hasDelay ? "ready" : "comingSoon",
        delayBelowS: hasDelay ? row.delay_below_30_s : null,
        delayDeltaS: hasDelay ? row.delay_above_30_s - row.delay_below_30_s : null,
        delayMultiplier: hasDelay ? row.delay_multiplier : null,
      };
    });
  }, [sensitivityRows]);

  const sortedCountries = useMemo(() => {
    const ready = countrySensitivity
      .filter((c) => c.status === "ready")
      .sort((a, b) =>
        countryBarMetric === "minutes"
          ? (b.delayDeltaS ?? 0) - (a.delayDeltaS ?? 0)
          : (b.delayIncreasePct ?? 0) - (a.delayIncreasePct ?? 0)
      );
    const comingSoon = countrySensitivity.filter((c) => c.status === "comingSoon");
    return [...ready, ...comingSoon];
  }, [countrySensitivity, countryBarMetric]);

  const maxDelayIncreasePct = useMemo(
    () =>
      Math.max(
        ...countrySensitivity
          .filter((c) => c.delayIncreasePct != null)
          .map((c) => c.delayIncreasePct!),
        1
      ),
    [countrySensitivity]
  );

  const maxCancellationIncreasePct = useMemo(
    () =>
      Math.max(
        ...countrySensitivity
          .filter((c) => c.cancellationIncreasePct != null)
          .map((c) => c.cancellationIncreasePct!),
        1
      ),
    [countrySensitivity]
  );

  const maxDelayMultiplier = useMemo(
    () =>
      Math.max(
        ...countrySensitivity
          .filter((c) => c.delayMultiplier != null)
          .map((c) => c.delayMultiplier!),
        1
      ),
    [countrySensitivity]
  );

  const maxDelayAboveS = useMemo(
    () =>
      Math.max(
        ...countrySensitivity
          .filter((c) => c.delayBelowS != null)
          .map((c) => c.delayBelowS! + c.delayDeltaS!),
        1
      ),
    [countrySensitivity]
  );

  const italyComparison = useMemo(
    () => countrySensitivity.find((country) => country.code === "IT"),
    [countrySensitivity]
  );

  const franceComparison = useMemo(
    () => countrySensitivity.find((country) => country.code === "FR"),
    [countrySensitivity]
  );

  useEffect(() => {
    window.scrollTo(0, 0);
    mixpanel.track("Heatwave Page Viewed");
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      const totalScroll = document.documentElement.scrollHeight - window.innerHeight;
      const pct = totalScroll > 0 ? (window.scrollY / totalScroll) * 100 : 0;
      if (scrollProgressRef.current) {
        scrollProgressRef.current.style.width = `${pct}%`;
      }
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    const observers: IntersectionObserver[] = [];
    const sectionIds = ["extraDelay", "comparison", "perDegree", "countries", "stationMap", "diveDeep"];

    sectionIds.forEach((id) => {
      const observer = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) {
            setVisibleSections((prev) => ({ ...prev, [id]: true }));
          }
        },
        { threshold: id === "countries" || id === "stationMap" || id === "diveDeep" ? 0.2 : 0.3 }
      );

      const element = document.getElementById(id);
      if (element) {
        const rect = element.getBoundingClientRect();
        if (rect.top < window.innerHeight && rect.bottom > 0) {
          setVisibleSections((prev) => ({ ...prev, [id]: true }));
        }
        observer.observe(element);
        observers.push(observer);
      }
    });

    return () => observers.forEach((obs) => obs.disconnect());
  }, []);

  return (
    <>
      <Helmet prioritizeSeoTags>
        <title>{t("heatwave.seo.title")}</title>
        <meta name="description" content={t("heatwave.seo.description")} />
        <link rel="canonical" href={HEATWAVE_SEO.url} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={HEATWAVE_SEO.url} />
        <meta property="og:title" content={t("heatwave.seo.title")} />
        <meta property="og:description" content={t("heatwave.seo.description")} />
        <meta property="og:image" content={HEATWAVE_SEO.image} />
        <meta property="twitter:card" content="summary_large_image" />
        <meta property="twitter:url" content={HEATWAVE_SEO.url} />
        <meta property="twitter:title" content={t("heatwave.seo.title")} />
        <meta property="twitter:description" content={t("heatwave.seo.description")} />
        <meta property="twitter:image" content={HEATWAVE_SEO.image} />
        <meta name="keywords" content={t("heatwave.seo.keywords")} />
        <meta name="robots" content="index, follow" />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebPage",
            name: t("heatwave.seo.title"),
            description: t("heatwave.seo.description"),
            url: "https://chuuchuu.com/heatwave",
            inLanguage: "en",
            isPartOf: { "@type": "WebSite", name: "chuuchuu", url: "https://chuuchuu.com" },
          })}
        </script>
        <link rel="preconnect" href="https://fonts.googleapis.com" />
        <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="anonymous" />
        <link
          href="https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700;800;900&display=swap"
          rel="stylesheet"
        />
      </Helmet>

      <PageContainer>
        <MeltSvgFilter />
        <SkyOverlay />
        <ScorchingSun aria-hidden>
          <SunRays />
          <SunCore />
        </ScorchingSun>
        <ScrollProgress ref={scrollProgressRef} />

        <HeroSection>
          <CloudField>
            {[
              { left: "8%", top: "18%", w: 120, h: 40 },
              { left: "25%", top: "8%", w: 160, h: 50 },
              { left: "55%", top: "22%", w: 100, h: 35 },
              { left: "70%", top: "12%", w: 140, h: 45 },
              { left: "15%", top: "35%", w: 90, h: 30 },
            ].map((cloud, i) => (
              <div
                key={i}
                style={{
                  left: cloud.left,
                  top: cloud.top,
                  width: `${cloud.w}px`,
                  height: `${cloud.h}px`,
                  animationDelay: `${i * 0.8}s`,
                }}
              />
            ))}
          </CloudField>
          <HeroContent>
            <HeroTitle>
              {(() => {
                const title = t("heatwave.hero.title");
                const lastSpace = title.lastIndexOf(" ");
                if (lastSpace === -1) return <MeltWord>{title}</MeltWord>;
                return (
                  <>
                    <HeroTitlePrefix>{title.slice(0, lastSpace)}</HeroTitlePrefix>{" "}
                    <MeltWord>{title.slice(lastSpace + 1)}</MeltWord>
                  </>
                );
              })()}
            </HeroTitle>
            <HeroSubtitle>{t("heatwave.hero.subtitle")}</HeroSubtitle>
            <ScrollHint>{t("heatwave.hero.scrollHint")}</ScrollHint>
          </HeroContent>
        </HeroSection>

        <Section id="extraDelay">
          <ContentCard visible={visibleSections.extraDelay}>
            <SectionTitle style={{ marginBottom: "3rem" }}>
              {t("heatwave.extraDelay.sectionTitle")}
            </SectionTitle>
            <BigNumberCard>
                <BigNumber>{t("heatwave.extraDelay.bigNumber")}</BigNumber>
                <BigNumberDescription>{t("heatwave.extraDelay.description")}</BigNumberDescription>
              </BigNumberCard>
            <KpiGrid>
              <KpiCard>
                <KpiValue>{t("heatwave.extraDelay.kpi1Value")}</KpiValue>
                <KpiLabel>{t("heatwave.extraDelay.kpi1Label")}</KpiLabel>
              </KpiCard>
              <KpiCard>
                <KpiValue>{t("heatwave.extraDelay.kpi2Value")}</KpiValue>
                <KpiLabel>{t("heatwave.extraDelay.kpi2Label")}</KpiLabel>
              </KpiCard>
            </KpiGrid>
            <DataDisclaimer>{t("heatwave.extraDelay.disclaimer")}</DataDisclaimer>
          </ContentCard>
        </Section>

        <Section id="comparison">
          <SectionWrapper maxWidth="80rem">
            <ContentCard visible={visibleSections.comparison}>
              <div style={{ textAlign: "center", marginBottom: "3rem" }}>
                <Icon name="thermometer" size={64} color={colors.orange400} />
                <SectionTitle style={{ marginTop: "1.5rem" }}>
                  {t("heatwave.comparison.sectionTitle")}
                </SectionTitle>
                <SectionSubtitle>{t("heatwave.comparison.sectionSubtitle")}</SectionSubtitle>
              </div>
              <ComparisonGrid>
                <DayCard variant="cool">
                  <DayLabel variant="cool">&lt;30°C</DayLabel>
                  <DayTitle>{t("heatwave.comparison.coolDays")}</DayTitle>
                  <DayStatsGrid>
                    <DayStatCard>
                      <StatValue color={colors.skyDeep}>{fmtStat(HEAT_STATS.coolDayAvgDelayMin)} min</StatValue>
                      <StatLabel>{t("heatwave.comparison.avgDelay")}</StatLabel>
                    </DayStatCard>
                    <DayStatCard>
                      <StatValue color={colors.skyDeep}>{fmtStat(HEAT_STATS.coolDayCancellationPct)}%</StatValue>
                      <StatLabel>{t("heatwave.comparison.cancellations")}</StatLabel>
                    </DayStatCard>
                  </DayStatsGrid>
                </DayCard>
                <DayCard variant="hot">
                  <DayLabel variant="hot">30°C+</DayLabel>
                  <DayTitle>{t("heatwave.comparison.hotDays")}</DayTitle>
                  <DayStatsGrid>
                    <DayStatCard>
                      <StatValue color={colors.orange400}>
                        {fmtStat(HEAT_STATS.hotDayAvgDelayMin)} min
                      </StatValue>
                      <StatLabel>{t("heatwave.comparison.avgDelay")}</StatLabel>
                    </DayStatCard>
                    <DayStatCard>
                      <StatValue color={colors.orange400}>
                        {fmtStatInt(HEAT_STATS.hotDayCancellationPct)}%
                      </StatValue>
                      <StatLabel>{t("heatwave.comparison.cancellations")}</StatLabel>
                    </DayStatCard>
                  </DayStatsGrid>
                </DayCard>
              </ComparisonGrid>
              <InfoBox>
                <InfoHeader>
                  <Icon name="flame" size={28} color={colors.orange400} />
                  <InfoTitle>{t("heatwave.comparison.infoTitle")}</InfoTitle>
                </InfoHeader>
                <InfoText>{t("heatwave.comparison.infoText")}</InfoText>
              </InfoBox>
            </ContentCard>
          </SectionWrapper>
        </Section>

        <Section id="perDegree">
          <SectionWrapper maxWidth="80rem">
            <ContentCard visible={visibleSections.perDegree}>
              <div style={{ textAlign: "center", marginBottom: "3rem" }}>
                <Icon name="trendingUp" size={64} color={colors.yellow400} />
                <SectionTitle style={{ marginTop: "1.5rem" }}>
                  {t("heatwave.perDegree.sectionTitle")}
                </SectionTitle>
                <SectionSubtitle>{t("heatwave.perDegree.sectionSubtitle")}</SectionSubtitle>
              </div>
              <ChartCard>
                <TemperatureDelayScatter visible={visibleSections.perDegree} />
              </ChartCard>
            </ContentCard>
          </SectionWrapper>
        </Section>

        <Section id="countries">
          <SectionWrapper maxWidth="80rem">
            <ContentCard visible={visibleSections.countries}>
              <div style={{ textAlign: "center", marginBottom: "3rem" }}>
                <Icon name="award" size={64} color={colors.yellow400} />
                <SectionTitle style={{ marginTop: "1.5rem" }}>
                  {t("heatwave.countries.sectionTitle")}
                </SectionTitle>
                <SectionSubtitle>{t("heatwave.countries.sectionSubtitle")}</SectionSubtitle>
              </div>
              {(italyComparison || franceComparison) && (
                <SensitivityGrid>
                  {italyComparison?.delayIncreasePct != null && (
                    <SensitivityCard variant="resilient" visible={!!visibleSections.countries}>
                      <div style={{ display: "flex", alignItems: "center", gap: "0.75rem", marginBottom: "1rem" }}>
                        <SensitivityEmoji aria-hidden>😎</SensitivityEmoji>
                        <SensitivityCardTitle variant="resilient">
                          {t("heatwave.countries.comparison.resilientLabel")}
                        </SensitivityCardTitle>
                      </div>
                      <div style={{ marginBottom: "1rem" }}>
                        <SensitivityCountryName>
                          {countryFlags.IT} {countryNames.IT}
                        </SensitivityCountryName>
                      </div>
                      <SensitivityStatsStack>
                        <DayStatCard>
                          <StatValue color={colors.skyDeep}>
                            +{italyComparison.delayIncreasePct}%
                          </StatValue>
                          <DelayMetricLabel
                            countryName={countryNames.IT}
                            pct={italyComparison.delayIncreasePct}
                            tooltipId="heatwave-delay-tooltip-IT-comparison"
                          />
                        </DayStatCard>
                        <DayStatCard>
                          <StatValue color={colors.skyDeep}>
                            {italyComparison.cancellationIncreasePct != null
                              ? `+${italyComparison.cancellationIncreasePct}%`
                              : "—"}
                          </StatValue>
                          {italyComparison.cancellationIncreasePct != null ? (
                            <CancellationMetricLabel
                              countryName={countryNames.IT}
                              pct={italyComparison.cancellationIncreasePct}
                              tooltipId="heatwave-cancellation-tooltip-IT-comparison"
                            />
                          ) : (
                            <StatLabel>{t("heatwave.countries.cancellationIncrease")}</StatLabel>
                          )}
                        </DayStatCard>
                      </SensitivityStatsStack>
                      <SensitivityDescription>
                        {t("heatwave.countries.comparison.resilientDescription")}
                      </SensitivityDescription>
                    </SensitivityCard>
                  )}
                  {franceComparison?.delayIncreasePct != null && (
                    <SensitivityCard variant="sensitive" visible={!!visibleSections.countries}>
                      <div style={{ display: "flex", alignItems: "center", gap: "0.75rem", marginBottom: "1rem" }}>
                        <SensitivityEmoji aria-hidden>🫠</SensitivityEmoji>
                        <SensitivityCardTitle variant="sensitive">
                          {t("heatwave.countries.comparison.sensitiveLabel")}
                        </SensitivityCardTitle>
                      </div>
                      <div style={{ marginBottom: "1rem" }}>
                        <SensitivityCountryName>
                          {countryFlags.FR} {countryNames.FR}
                        </SensitivityCountryName>
                      </div>
                      <SensitivityStatsStack>
                        <DayStatCard>
                          <StatValue color={colors.red600}>
                            +{franceComparison.delayIncreasePct}%
                          </StatValue>
                          <DelayMetricLabel
                            countryName={countryNames.FR}
                            pct={franceComparison.delayIncreasePct}
                            tooltipId="heatwave-delay-tooltip-FR-comparison"
                          />
                        </DayStatCard>
                        <DayStatCard>
                          <StatValue color={colors.red600}>
                            {franceComparison.cancellationIncreasePct != null
                              ? `+${franceComparison.cancellationIncreasePct}%`
                              : "—"}
                          </StatValue>
                          {franceComparison.cancellationIncreasePct != null ? (
                            <CancellationMetricLabel
                              countryName={countryNames.FR}
                              pct={franceComparison.cancellationIncreasePct}
                              tooltipId="heatwave-cancellation-tooltip-FR-comparison"
                            />
                          ) : (
                            <StatLabel>{t("heatwave.countries.cancellationIncrease")}</StatLabel>
                          )}
                        </DayStatCard>
                      </SensitivityStatsStack>
                      <SensitivityDescription>
                        {t("heatwave.countries.comparison.sensitiveDescription")}
                      </SensitivityDescription>
                    </SensitivityCard>
                  )}
                </SensitivityGrid>
              )}
              <CountryMetricSwitch>
                <CountryMetricSwitchButton
                  type="button"
                  $active={countryBarMetric === "relative"}
                  onClick={() => {
                    mixpanel.track("Heatwave Country Bar Metric Switched", { metric: "relative" });
                    setCountryBarMetric("relative");
                  }}
                  aria-pressed={countryBarMetric === "relative"}
                >
                  <Percent size={15} strokeWidth={2} aria-hidden />
                  {t("heatwave.map.switchRelative")}
                </CountryMetricSwitchButton>
                <CountryMetricSwitchButton
                  type="button"
                  $active={countryBarMetric === "minutes"}
                  onClick={() => {
                    mixpanel.track("Heatwave Country Bar Metric Switched", { metric: "minutes" });
                    setCountryBarMetric("minutes");
                  }}
                  aria-pressed={countryBarMetric === "minutes"}
                >
                  <Clock size={15} strokeWidth={2} aria-hidden />
                  {t("heatwave.map.switchMinutes")}
                </CountryMetricSwitchButton>
              </CountryMetricSwitch>
              <CountryList>
                {sortedCountries.map((country, index) => {
                  const isReady = country.status === "ready";
                  const rank = isReady ? index + 1 : 0;
                  const hasCancellation = country.cancellationIncreasePct != null;

                  return (
                    <motion.div
                      key={country.code}
                      layout="position"
                      transition={{ type: "spring", stiffness: 400, damping: 40 }}
                    >
                    <CountryCardShell
                      visible={visibleSections.countries}
                      delay={index * 100}
                    >
                      <CountryCard dimmed={!isReady}>
                      <CountryHeader>
                        {isReady ? (
                          <RankNumber rank={rank}>#{rank}</RankNumber>
                        ) : (
                          <RankNumber rank={99}>—</RankNumber>
                        )}
                        <CountryInfo>
                          <div>
                            {countryFlags[country.code]} {countryNames[country.code]}
                            {!isReady && (
                              <ComingSoonBadge>{t("heatwave.countries.comingSoon")}</ComingSoonBadge>
                            )}
                          </div>
                          {isReady && country.delayMultiplier != null && (
                            <ProgressBar>
                              <ProgressFillBlue
                                width={
                                  countryBarMetric === "relative"
                                    ? (1.0 / maxDelayMultiplier) * 100
                                    : (country.delayBelowS! / maxDelayAboveS) * 100
                                }
                                visible={visibleSections.countries}
                                delay={index * 100}
                                data-tooltip-id="heatwave-bar-blue"
                                data-tooltip-content={t("heatwave.countries.barTooltipBelow", {
                                  min: fmtStat(country.delayBelowS! / 60),
                                })}
                              />
                              <ProgressFill
                                width={
                                  countryBarMetric === "relative"
                                    ? ((country.delayMultiplier - 1.0) / maxDelayMultiplier) * 100
                                    : (country.delayDeltaS! / maxDelayAboveS) * 100
                                }
                                visible={visibleSections.countries}
                                delay={index * 100}
                                data-tooltip-id="heatwave-bar-orange"
                                data-tooltip-content={t("heatwave.countries.barTooltipAbove", {
                                  min: fmtStat(country.delayDeltaS! / 60),
                                })}
                              />
                            </ProgressBar>
                          )}
                        </CountryInfo>
                        <CountryStats>
                          <CountryStat>
                            {isReady ? (
                              <HeatPctValue
                                $intensity={heatPctIntensity(
                                  country.delayIncreasePct!,
                                  maxDelayIncreasePct
                                )}
                              >
                                {countryBarMetric === "relative"
                                  ? `+${country.delayIncreasePct}%`
                                  : `+${fmtStat(country.delayDeltaS! / 60)} min`}
                              </HeatPctValue>
                            ) : (
                              <MutedPctValue>—</MutedPctValue>
                            )}
                            {isReady && country.delayIncreasePct != null ? (
                              <DelayMetricLabel
                                countryName={countryNames[country.code]}
                                pct={country.delayIncreasePct}
                                tooltipId={`heatwave-delay-tooltip-${country.code}-bar`}
                                variant="bar"
                                minDiff={
                                  countryBarMetric === "minutes"
                                    ? country.delayDeltaS! / 60
                                    : undefined
                                }
                              />
                            ) : (
                              <CountryStatLabel>{t("heatwave.countries.delayIncrease")}</CountryStatLabel>
                            )}
                          </CountryStat>
                          <CountryStat>
                            {isReady && hasCancellation ? (
                              <HeatPctValue
                                $intensity={heatPctIntensity(
                                  country.cancellationIncreasePct!,
                                  maxCancellationIncreasePct
                                )}
                              >
                                +{country.cancellationIncreasePct}%
                              </HeatPctValue>
                            ) : (
                              <MutedPctValue>—</MutedPctValue>
                            )}
                            {isReady && hasCancellation ? (
                              <CancellationMetricLabel
                                countryName={countryNames[country.code]}
                                pct={country.cancellationIncreasePct!}
                                tooltipId={`heatwave-cancellation-tooltip-${country.code}-bar`}
                                variant="bar"
                              />
                            ) : isReady && country.code === "HU" ? (
                              <CancellationUnavailableMetricLabel
                                countryName={countryNames.HU}
                                tooltipId="heatwave-cancellation-unavailable-HU-bar"
                                variant="bar"
                              />
                            ) : (
                              <CountryStatLabel>{t("heatwave.countries.cancellationIncrease")}</CountryStatLabel>
                            )}
                          </CountryStat>
                        </CountryStats>
                      </CountryHeader>
                      </CountryCard>
                    </CountryCardShell>
                    </motion.div>
                  );
                })}
              </CountryList>
              <StyledTooltip id="heatwave-bar-blue" place="top" className="tooltip-styled" />
              <StyledTooltip id="heatwave-bar-orange" place="top" className="tooltip-styled" />
            </ContentCard>
          </SectionWrapper>
        </Section>

        <Section id="stationMap">
          <SectionWrapper maxWidth="80rem">
            <ContentCard visible={visibleSections.stationMap}>
              <div style={{ textAlign: "center", marginBottom: "3rem" }}>
                <Icon name="mapPin" size={64} color={colors.skyDeep} />
                <SectionTitle style={{ marginTop: "1.5rem" }}>
                  {t("heatwave.map.sectionTitle")}
                </SectionTitle>
                <SectionSubtitle>{t("heatwave.map.sectionSubtitle")}</SectionSubtitle>
              </div>
              <HeatwaveStationMap visible={!!visibleSections.stationMap} />
              <DiveDeepMetricNotes>
                <p>{t("heatwave.map.metricNotes.temperature")}</p>
                <p>{t("heatwave.map.metricNotes.insufficientData")}</p>
              </DiveDeepMetricNotes>
            </ContentCard>
          </SectionWrapper>
        </Section>

        <Section id="diveDeep">
          <SectionWrapper maxWidth="80rem">
            <ContentCard visible={visibleSections.diveDeep}>
              <div style={{ textAlign: "center", marginBottom: "3rem" }}>
                <Icon name="lineChart" size={64} color={colors.yellow400} />
                <SectionTitle style={{ marginTop: "1.5rem" }}>
                  {t("heatwave.diveDeep.sectionTitle")}
                </SectionTitle>
                <SectionSubtitle>{t("heatwave.diveDeep.sectionSubtitle")}</SectionSubtitle>
              </div>
              <ChartCard>
                <DelayTemperatureTimeSeries visible={visibleSections.diveDeep} />
              </ChartCard>
              <DiveDeepMetricNotes>
                <p>{t("heatwave.diveDeep.metricNotes.dataCoverage")}</p>
                <p>{t("heatwave.diveDeep.metricNotes.temperature")}</p>
                <p>{t("heatwave.diveDeep.metricNotes.averageDelay")}</p>
                <p>{t("heatwave.diveDeep.metricNotes.cancellationRate")}</p>
              </DiveDeepMetricNotes>
            </ContentCard>
          </SectionWrapper>
        </Section>

        <ClosingSection>
          <SectionWrapper maxWidth="48rem">
            <div style={{ textAlign: "center", zIndex: 10, position: "relative" }}>
              <div style={{ marginBottom: "2rem", display: "flex", justifyContent: "center" }}>
                <Icon name="sun" size={80} color={colors.orange400} />
              </div>
              <h2 style={{ fontSize: "2.25rem", fontWeight: 700, marginBottom: "1.5rem", color: colors.textDark }}>
                {t("heatwave.closing.title")}
              </h2>
              <p
                style={{
                  fontSize: "1.25rem",
                  color: colors.textMid,
                  marginBottom: "1.5rem",
                  lineHeight: 1.75,
                }}
              >
                {t("heatwave.closing.description")}
              </p>
              <CTACard>
                <CTATitle>{t("heatwave.closing.ctaTitle")}</CTATitle>
                <CTASubtitle>{t("heatwave.closing.ctaSubtitle")}</CTASubtitle>
                <CTAButton onClick={() => { mixpanel.track("Heatwave CTA Clicked", { cta: "primary" }); navigate("/"); }}>{t("heatwave.closing.ctaButton")}</CTAButton>
              </CTACard>
              <SecondaryCTACard>
                <CTATitle style={{ fontSize: "1.25rem" }}>
                  {t("heatwave.closing.secondaryCtaTitle")}
                </CTATitle>
                <CTASubtitle style={{ fontSize: "0.875rem", marginBottom: "1.5rem" }}>
                  {t("heatwave.closing.secondaryCtaSubtitle")}
                </CTASubtitle>
                <SecondaryCTAButton onClick={() => { mixpanel.track("Heatwave CTA Clicked", { cta: "secondary" }); navigate("/data"); }}>
                  {t("heatwave.closing.secondaryCtaButton")}
                </SecondaryCTAButton>
              </SecondaryCTACard>
            </div>
          </SectionWrapper>
        </ClosingSection>
      </PageContainer>
    </>
  );
};

export default HeatwavePage;
